import type {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  FeatureServices,
  Logger,
  SharedFeatureService,
} from '@feature-hub/core';
export type {
  Endpoint,
  EndpointName,
  Endpoints,
} from './rest-api/create-endpoints.js';

export interface EndpointDirectoryV1 {
  readonly envName: EnvNameV1;
  readonly preview: boolean;
  readonly restApiAuthHeader?: HeaderV1;
}

export type EnvNameV1 = 'prod' | 'stage' | 'dev';

export interface HeaderV1 {
  readonly headerName: string;
  readonly headerValue: string;
}

export interface EndpointDirectoryV2 {
  readonly envName: EnvNameV2;
  readonly preview: boolean;

  /**
   * Used for fetching endpoints within protected environments.
   */
  readonly rotatingVerifySecret?: HeaderV2;
}

export type EnvNameV2 = EnvNameV1;
export type HeaderV2 = HeaderV1;

export interface EndpointDirectoryV3 {
  readonly envName: EnvNameV3;
  readonly preview: boolean;

  /**
   * Used for fetching endpoints within protected environments.
   */
  readonly rotatingVerifySecret?: HeaderV1;
}

export type EnvNameV3 = EnvNameV2 | 'sit';
export type HeaderV3 = HeaderV2;

export interface SharedEndpointDirectory extends SharedFeatureService {
  readonly '1.0.0': FeatureServiceBinder<EndpointDirectoryV1>;
  readonly '2.0.0': FeatureServiceBinder<EndpointDirectoryV2>;
  readonly '3.0.0': FeatureServiceBinder<EndpointDirectoryV3>;
}

export interface LocaleServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
}

export function defineEndpointDirectory(
  endpointDirectory: EndpointDirectoryV3,
): FeatureServiceProviderDefinition<
  SharedEndpointDirectory,
  LocaleServiceDependencies
> {
  const {envName, rotatingVerifySecret, preview} = endpointDirectory;

  return {
    id: `endpoint-directory`,
    optionalDependencies: {
      featureServices: {
        's2:logger': `^1.0.0`,
      },
    },
    create: ({featureServices}) => {
      const {'s2:logger': logger = console} = featureServices;

      return {
        '1.0.0': (consumerId, consumerName) => {
          assertIsEnvNameV1(envName, consumerId, consumerName);

          logger.warn(
            `DEPRECATED: Version 1.0.0 of the endpoint directory is deprecated. The consumer ${printConsumerInfo(
              {consumerId, consumerName},
            )} should use 3.0.0 instead.`,
          );

          return {
            featureService: {
              envName,
              preview,
              restApiAuthHeader: rotatingVerifySecret,
            },
          };
        },
        '2.0.0': (consumerId, consumerName) => {
          assertIsEnvNameV1(envName, consumerId, consumerName);

          logger.warn(
            `DEPRECATED: Version 2.0.0 of the endpoint directory is deprecated. The consumer ${printConsumerInfo(
              {consumerId, consumerName},
            )} should use 3.0.0 instead.`,
          );

          return {featureService: {envName, preview, rotatingVerifySecret}};
        },
        '3.0.0': () => ({featureService: endpointDirectory}),
      };
    },
  };
}

function assertIsEnvNameV1(
  envName: EnvNameV3,
  consumerId: string,
  consumerName: string | undefined,
): asserts envName is EnvNameV1 {
  if (envName === `sit`) {
    throw new Error(
      `The consumer ${printConsumerInfo({
        consumerId,
        consumerName,
      })} must use 3.0.0 of the endpoint directory to support the SIT environment.`,
    );
  }
}

interface ConsumerInfo {
  readonly consumerId: string;
  readonly consumerName: string | undefined;
}

function printConsumerInfo({consumerId, consumerName}: ConsumerInfo): string {
  return `"${consumerId}"${consumerName ? ` ("${consumerName}")` : ``}`;
}
